import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable, TableSkeleton } from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { LabeledPillList } from '@/components/common/LabeledPill';
import type { LabeledStat } from '@/components/common/StatCardGrid';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { Button } from '@/components/ui/button';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getActiveStatCard, getInactiveStatCard } from '@/utils/activeStatCards';
import {
  assignmentsQueryOptions,
  monitorDetailInfiniteQueryOptions,
  questionProjectQueryOptions,
  questionProjectsInfiniteQueryOptions,
} from '../api/question.api';
import { AssignmentPanel } from '../components/AssignmentPanel';
import { getInstitutePapersLink } from '../components/monitorLevelLinks';
import { QuestionProjectFormModal } from '../components/QuestionProjectFormModal';
import { PROJECT_MANAGEMENT_PATH, type ProjectTabId } from '../constants/question.constants';
import { useDeleteQuestionProject } from '../hooks/useQuestionMutations';
import { useProjectManagementView } from '../hooks/useProjectManagementView';
import { useQuestionProjectFilters } from '../hooks/useQuestionProjectFilters';
import { getMonitorDetailColumns, getProjectColumns } from '../components/QuestionColumns';
import type { QuestionProject, QuestionProjectResponse } from '../types/question.types';
import { getProjectPillFields } from '../utils/projectPills';

const ASSIGNMENT_COLUMN_COUNT = 8;

function getProjectStatCards(meta: QuestionProjectResponse['meta'] | undefined): LabeledStat[] {
  return [
    { key: 'projects', label: 'Projects', value: meta?.total },
    getActiveStatCard(meta?.totals),
    getInactiveStatCard(meta?.totals),
    {
      key: 'institutes',
      label: 'Institutes',
      value: meta?.totals.institutes,
    },
  ];
}

export function QuestionProjectsPage() {
  const controller = useQuestionProjectFilters();
  const { search } = controller;
  const view = useProjectManagementView();
  const queryClient = useQueryClient();

  const projects = useInfiniteTableQuery(questionProjectsInfiniteQueryOptions(search));
  const openProjectQuery = useQuery(questionProjectQueryOptions(view.openProjectId));
  const lastViewedPapersId = useRevealLastViewedRow('questionInstitutePapers');

  const [editing, setEditing] = useState<QuestionProject | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<QuestionProject | null>(null);

  const remove = useDeleteQuestionProject(() => setDeleting(null));

  const hasOpenProject = view.openProjectId !== undefined;
  const institutes = useInfiniteTableQuery(
    monitorDetailInfiniteQueryOptions(
      view.openProjectId === undefined ? {} : { projectId: view.openProjectId },
      true,
    ),
  );
  const assignments = useQuery(assignmentsQueryOptions(view.openProjectId, view.assignmentUserId));
  const hasAssignmentPick = hasOpenProject && view.assignmentUserId !== undefined;

  function handleProjectOpen(project: QuestionProject) {
    queryClient.setQueryData(questionProjectQueryOptions(project.projectId).queryKey, project);
    void view.openProject(project.projectId);
  }

  function handleProjectClose() {
    void view.closeProject();
  }

  const institutePaperColumns = useMemo(
    () =>
      getMonitorDetailColumns((row) =>
        getInstitutePapersLink(row.projectInstituteId, {
          from: PROJECT_MANAGEMENT_PATH,
          projectId: row.projectId ?? undefined,
          userId: row.userId ?? undefined,
          instituteId: row.instituteId ?? undefined,
        }),
      ),
    [],
  );

  function openForm(project: QuestionProject | null) {
    setEditing(project);
    setFormOpen(true);
  }

  const tabs: TableTab<ProjectTabId>[] = [
    {
      id: 'projects',
      label: 'Projects',
      count: projects.firstPageMeta?.total,
      isCountLoading: projects.isLoading,
    },
    {
      id: 'assignments',
      label: 'Assignments',
      count: hasAssignmentPick ? assignments.data?.length : 0,
      isCountLoading: hasAssignmentPick && assignments.isLoading,
    },
    {
      id: 'institutes',
      label: 'Institutes',
      count: hasOpenProject ? institutes.firstPageMeta?.total : 0,
      isCountLoading: hasOpenProject && institutes.isLoading,
    },
  ];

  function renderProjectsTable() {
    if (projects.loadError) {
      return (
        <ErrorState
          error={projects.loadError}
          fallbackTitle="Could not load the projects"
          onRetry={() => void projects.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={projects.rows}
        columns={getProjectColumns(
          {
            onOpenInstitutes: handleProjectOpen,
            onEdit: openForm,
            onDelete: setDeleting,
          },
          search.search,
        )}
        isLoading={projects.isLoading}
        className="lg:flex-1"
        stickyColumnId="name"
        emptyMessage="No project matches"
        {...projects.loadMoreProps}
      />
    );
  }

  function renderInstitutesTable() {
    if (institutes.loadError) {
      return (
        <ErrorState
          error={institutes.loadError}
          fallbackTitle="Could not load the institutes"
          onRetry={() => void institutes.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={institutes.rows}
        columns={institutePaperColumns}
        isLoading={institutes.isLoading}
        className="lg:flex-1"
        stickyColumnId="instituteName"
        emptyMessage="No institute is on this project"
        isRowHighlighted={(row) => row.projectInstituteId === lastViewedPapersId}
        {...institutes.loadMoreProps}
      />
    );
  }

  function renderAssignmentPanel(project: QuestionProject | undefined) {
    if (project === undefined) {
      return <TableSkeleton columnCount={ASSIGNMENT_COLUMN_COUNT} />;
    }
    return (
      <AssignmentPanel
        project={project}
        userId={view.assignmentUserId}
        onUserIdChange={(userId) => void view.changeAssignmentUser(userId)}
      />
    );
  }

  function renderOpenProjectTab() {
    if (openProjectQuery.error) {
      return (
        <ErrorState
          error={openProjectQuery.error}
          fallbackTitle="Could not load the project"
          onRetry={() => void openProjectQuery.refetch()}
          fill
        />
      );
    }
    if (openProjectQuery.data === null) {
      return (
        <EmptyState
          title="This project is no longer here"
          description="It may have been deleted. Open another project from the Projects tab."
          actions={
            <Button type="button" variant="outline" size="sm" onClick={handleProjectClose}>
              Go to Projects
            </Button>
          }
          fill
        />
      );
    }

    const project = openProjectQuery.data;
    return (
      <>
        <div className="flex items-center justify-between gap-3">
          <LabeledPillList fields={getProjectPillFields(project)} />
          <Button type="button" size="sm" variant="outline" onClick={handleProjectClose}>
            <X className="size-4" />
            Close
          </Button>
        </div>
        {view.activeTabId === 'assignments'
          ? renderAssignmentPanel(project)
          : renderInstitutesTable()}
      </>
    );
  }

  function renderActiveTab() {
    if (view.activeTabId === 'projects') return renderProjectsTable();
    if (!hasOpenProject) {
      return (
        <EmptyState
          kind="pickFirst"
          title="Open a project"
          description="Open a project from the Projects tab to see it here."
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void view.changeTab('projects')}
            >
              Go to Projects
            </Button>
          }
          fill
        />
      );
    }
    return renderOpenProjectTab();
  }

  return (
    <CatalogueListLayout
      title="Project Management"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search projects',
      }}
      filters={controller}
      stats={{
        cards: getProjectStatCards(projects.firstPageMeta),
        isLoading: projects.isLoading,
        isFetching: projects.isRefetching,
        isError: projects.loadError !== null,
      }}
      onAdd={() => openForm(null)}
      addLabel="New Project"
    >
      <TableTabs
        tabs={tabs}
        activeTabId={view.activeTabId}
        onTabChange={(tab) => void view.changeTab(tab)}
        ariaLabel="Project tables"
      />

      {renderActiveTab()}

      <QuestionProjectFormModal project={editing} open={formOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Delete this project?"
        description={`"${deleting?.name.trim() ?? 'This project'}" will be deleted. It cannot be deleted while institutes are still assigned to it. Take them off first.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => {
          if (deleting !== null) remove.mutate(deleting.projectId);
        }}
      />
    </CatalogueListLayout>
  );
}
